import { Link } from 'react-router-dom';
import {
  ImageIcon,
  KeyRound,
  DollarSign,
  ShieldAlert,
  ShieldCheck,
  Layers,
  Inbox,
  BarChart3,
  ReceiptText,
  Gauge,
  Building2,
  Mail,
  Hammer,
  Globe,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { NotificationBadge } from '@/components/ui/notification-badge';
import { usePendingPluginReviewCount } from '../hooks/usePendingPluginReviewCount';
import { useProviderIssueCount } from '../hooks/useProviderIssueCount';
import { analytics } from '@/lib/analytics';

const REVIEW_QUEUE_PATH = '/dev-console/admin/reviews';
const PROVIDERS_PATH = '/dev-console/providers';

export function Dashboard() {
  const pendingReviews = usePendingPluginReviewCount();
  const providerIssues = useProviderIssueCount();
  const tools = [
    {
      name: 'Arche Icon Manager',
      description: 'Upload and manage icons for arches',
      path: '/dev-console/arche-icons',
      icon: ImageIcon,
    },
    {
      name: 'Fias Roles',
      description:
        'Global platform roles and who holds them — grant, revoke, and review who has privileged access',
      path: '/dev-console/roles',
      icon: ShieldCheck,
    },
    {
      name: 'Beta Key Manager',
      description: 'Generate and manage invite keys for closed beta participants',
      path: '/dev-console/beta-keys',
      icon: KeyRound,
    },
    {
      name: 'Payments & Revenue',
      description: 'Platform economics, revenue, costs, refunds, and user management',
      path: '/dev-console/payments',
      icon: DollarSign,
    },
    {
      name: 'Credit Activity',
      description:
        'Platform-wide log of admin credit grants, deductions, and refunds — who, to whom, how much, when',
      path: '/dev-console/credit-activity',
      icon: ReceiptText,
    },
    {
      name: 'Platform Usage',
      description:
        'DAU/MAU, where users spend their time and credits, entity health, daily-burn-cap proximity, per-user activity drill-down',
      path: '/dev-console/analytics',
      icon: BarChart3,
    },
    {
      name: 'Providers',
      description:
        'Upstream provider spend caps & safety — per-provider daily/monthly cap circuit breaker, spend vs cap, and billing-account metadata',
      path: '/dev-console/providers',
      icon: Gauge,
    },
    {
      name: 'Arche Management',
      description: 'Manage featured arches and per-arche suggested arches',
      path: '/dev-console/arche-management',
      icon: Layers,
    },
    {
      name: 'Web Sources',
      description:
        'Sites Fias approved for apps to read as data, and the switch that lets apps use them',
      path: '/dev-console/web-sources',
      icon: Globe,
    },
    {
      name: 'Fias Plugins',
      description:
        'Annex a developer plugin into Fias ownership and set who on the Fias workspace can read or publish it',
      path: '/dev-console/plugin-annexation',
      icon: Building2,
    },
    {
      name: 'Plugin Review Queue',
      description:
        'Review escalated plugin submissions — approve to release the build, or reject with notes',
      path: '/dev-console/admin/reviews',
      icon: Inbox,
    },
    {
      name: 'Companies',
      description:
        'Partner Companies under their own name — grant branding and own-domain email, email health, pause/resume, and a kill switch',
      path: '/dev-console/companies',
      icon: Mail,
    },
    {
      name: 'Fias Forge',
      description:
        'Hosted build environments — kill switch and metering, fleet metrics, user holds, and every forge with force-stop and archive',
      path: '/dev-console/forge',
      icon: Hammer,
    },
    {
      name: 'Maintenance Mode',
      description: 'Emergency kill switch — take the platform offline for all non-admin users',
      path: '/dev-console/maintenance',
      icon: ShieldAlert,
    },
  ];

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Dev Console</h1>
        <p className="mt-2 text-muted-foreground">
          Development tools for testing and previewing Fias components
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {tools.map((tool) => {
          const IconComponent = tool.icon;
          return (
            <Link
              key={tool.path}
              to={tool.path}
              onClick={() => analytics.featureUsed('dev_console_tool_open', { tool: tool.name })}
            >
              <Card className="h-full transition-shadow hover:shadow-lg">
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="text-primary">
                      <IconComponent className="h-8 w-8" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{tool.name}</CardTitle>
                        {tool.path === REVIEW_QUEUE_PATH && (
                          <NotificationBadge
                            count={pendingReviews}
                            aria-label="Arches awaiting review"
                          />
                        )}
                        {tool.path === PROVIDERS_PATH && (
                          <NotificationBadge
                            count={providerIssues}
                            aria-label="Providers with active issues"
                          />
                        )}
                      </div>
                      <CardDescription className="mt-1">{tool.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default Dashboard;
